import QRCodeStyling from 'qr-code-styling';
import { QRDesign, QRExportSettings } from '@/types/qr';
import { createQRStylingOptions } from './styling';
import { renderFramedCanvas } from './frames';

/**
 * Generates a high-resolution Blob for the QR code, applying frames if configured.
 */
export async function getQRBlob(
  data: string,
  design: QRDesign,
  size: number = 1024,
  format: 'png' | 'jpeg' | 'webp' | 'svg' = 'png'
): Promise<Blob> {
  const qrOptions = createQRStylingOptions(data || 'https://qr.planmaster.in', design, size);

  if (format === 'svg') {
    const qrInstance = new QRCodeStyling({ ...qrOptions, type: 'svg' });
    const rawSvgBlob = await qrInstance.getRawData('svg');
    if (!rawSvgBlob) {
      throw new Error('Failed to generate SVG blob');
    }
    return rawSvgBlob as Blob;
  }

  // Generate PNG raw data via qr-code-styling
  const qrInstance = new QRCodeStyling({ ...qrOptions, type: 'canvas' });
  const rawPngBlob = (await qrInstance.getRawData('png')) as Blob | null;

  if (!rawPngBlob) {
    throw new Error('Failed to render QR Code blob');
  }

  // If no frame is configured and format is PNG, return the raw blob directly
  if ((!design.frame || design.frame.type === 'none') && format === 'png') {
    return rawPngBlob;
  }

  // If a frame is configured or format is JPEG/WebP, composite using an offscreen canvas
  const img = new Image();
  const imgUrl = URL.createObjectURL(rawPngBlob);

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = (e) => reject(e);
    img.src = imgUrl;
  });

  const framedCanvas = await renderFramedCanvas(img, design.frame, size);
  URL.revokeObjectURL(imgUrl);

  const mimeMap: Record<string, string> = {
    png: 'image/png',
    jpeg: 'image/jpeg',
    webp: 'image/webp'
  };
  const mimeType = mimeMap[format] || 'image/png';

  return new Promise<Blob>((resolve, reject) => {
    framedCanvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to Blob'));
        }
      },
      mimeType,
      0.95
    );
  });
}

/**
 * Composites and returns an HTMLCanvasElement (for preview, clipboard, or JSZip batch generation).
 */
export async function getCompositedCanvas(
  data: string,
  design: QRDesign,
  size: number = 1024
): Promise<HTMLCanvasElement> {
  const blob = await getQRBlob(data, design, size, 'png');
  const img = new Image();
  const imgUrl = URL.createObjectURL(blob);

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = (e) => reject(e);
    img.src = imgUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || size;
  canvas.height = img.naturalHeight || size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.drawImage(img, 0, 0);
  }
  URL.revokeObjectURL(imgUrl);

  return canvas;
}

/**
 * Exports the QR code in the requested format (PNG, SVG, JPEG, WebP) and triggers browser download.
 */
export async function downloadQRCode(
  data: string,
  design: QRDesign,
  settings: QRExportSettings
): Promise<void> {
  const safeFilename = (settings.filename || 'qr-code').replace(/[^a-zA-Z0-9_\-\.]/g, '_');
  const size = settings.size || 1024;
  const format = settings.format || 'png';

  const blob = await getQRBlob(data, design, size, format);
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeFilename}.${format}`;
  a.style.display = 'none';
  document.body.appendChild(a);

  // Trigger click
  a.click();

  // Cleanup with delay to support iOS Safari and slower mobile downloads
  setTimeout(() => {
    if (document.body.contains(a)) {
      document.body.removeChild(a);
    }
    URL.revokeObjectURL(url);
  }, 2000);
}

/**
 * Copies the rendered QR code as a PNG image directly to the system clipboard.
 */
export async function copyQRImageToClipboard(
  data: string,
  design: QRDesign
): Promise<boolean> {
  try {
    const blob = await getQRBlob(data, design, 1024, 'png');

    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      return true;
    }
    return false;
  } catch (err) {
    console.warn('Failed to copy QR image to clipboard:', err);
    return false;
  }
}

/**
 * Copies text string to clipboard.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch {
    return false;
  }
}

/**
 * Shares the QR code using the native Web Share API where supported.
 */
export async function shareQRCode(
  data: string,
  design: QRDesign,
  title: string = 'QR Code'
): Promise<boolean> {
  try {
    const blob = await getQRBlob(data, design, 1024, 'png');
    const file = new File([blob], 'qr-code.png', { type: 'image/png' });

    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title,
          text: 'Generated with QR Studio',
          files: [file]
        });
        return true;
      } catch (err: unknown) {
        if ((err as Error)?.name === 'AbortError') {
          return true; // User cancelled share sheet
        }
        return false;
      }
    } else if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: data
        });
        return true;
      } catch {
        return false;
      }
    } else {
      return await copyQRImageToClipboard(data, design);
    }
  } catch (err) {
    console.error('Share failed:', err);
    return false;
  }
}
